import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import './ContractDetail.css';
import { getContractApiUrl } from '../utils/apiUrls';

const BACKEND_URL = getContractApiUrl();

const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];

const PERIOD_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const CHART_GROUPINGS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const CHART_METRICS = [
  { value: 'emails_processed', label: 'Emails processed' },
  { value: 'contracts_opened', label: 'Contracts opened' },
  { value: 'contracts_closed', label: 'Contracts closed' },
  { value: 'splits_processed', label: 'Split attachments processed' },
  { value: 'forms_generated', label: 'Forms generated' },
  { value: 'user_logins', label: 'User logins' },
  { value: 'unique_user_logins', label: 'Unique user logins' },
  { value: 'unique_users_new_contract', label: 'Unique users with new contract' },
  { value: 'unique_users_emailing', label: 'Unique users emailing KeyFlo' },
];

const TABLE_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'product_display', label: 'Product' },
  { key: 'payment_user_type', label: 'Payment Type' },
  { key: 'created_at', label: 'Joined', sortKey: 'created_at' },
  { key: 'contracts_lifetime', label: 'Contracts (life)' },
  { key: 'emails_lifetime', label: 'Emails (life)' },
  { key: 'contracts_90d', label: 'Contracts (90d)' },
  { key: 'emails_90d', label: 'Emails (90d)' },
];

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '1px solid #e5e7eb',
  background: '#f8fafc',
  position: 'sticky',
  top: 0,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'top',
};

const tableWrapStyle = {
  overflowX: 'auto',
  background: '#ffffff',
  border: '1px solid #e5e7eb',
};

const linkStyle = {
  color: '#2563eb',
  cursor: 'pointer',
  textDecoration: 'underline',
};

function centsToDisplay(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

function formatDateShort(iso) {
  if (!iso) return '—';
  return String(iso).slice(0, 10);
}

function DeveloperUserAnalytics() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [sort, setSort] = useState('contracts_90d');
  const [order, setOrder] = useState('desc');
  const [period, setPeriod] = useState('90d');
  const [chartGrouping, setChartGrouping] = useState('daily');
  const [chartMetric, setChartMetric] = useState('emails_processed');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [factSheet, setFactSheet] = useState(null);
  const [factLoading, setFactLoading] = useState(false);

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    return ALLOWED_USERS.includes(email);
  }, [user]);

  const callerEmail = user?.email || '';

  const apiParams = useCallback((extra = {}) => ({
    caller_email: callerEmail,
    ...extra,
  }), [callerEmail]);

  const fetchOverview = useCallback(async () => {
    const resp = await axios.get(`${BACKEND_URL}/dev/user-analytics/overview`, {
      params: apiParams({
        period,
        chart_grouping: chartGrouping,
        chart_metric: chartMetric,
        chart_days: 90,
      }),
    });
    if (resp.data?.status !== 'success') {
      throw new Error(resp.data?.error || 'Failed to load overview');
    }
    setOverview(resp.data);
  }, [apiParams, period, chartGrouping, chartMetric]);

  const fetchUsers = useCallback(async () => {
    const resp = await axios.get(`${BACKEND_URL}/dev/user-analytics/users`, {
      params: apiParams({ sort, order, limit: 20 }),
    });
    if (resp.data?.status !== 'success') {
      throw new Error(resp.data?.error || 'Failed to load users');
    }
    setUsers(resp.data.users || []);
  }, [apiParams, sort, order]);

  const fetchFactSheet = useCallback(async (userId) => {
    setFactLoading(true);
    try {
      const resp = await axios.get(`${BACKEND_URL}/dev/user-analytics/users/${userId}`, {
        params: apiParams(),
      });
      if (resp.data?.status !== 'success') {
        throw new Error(resp.data?.error || 'Failed to load user');
      }
      setFactSheet(resp.data);
      setSelectedUserId(userId);
    } catch (e) {
      setError(e.message || 'Failed to load user fact sheet');
    } finally {
      setFactLoading(false);
    }
  }, [apiParams]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) navigate('/');
  }, [isAuthenticated, isKeyFloUser, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !isKeyFloUser) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        await Promise.all([fetchOverview(), fetchUsers()]);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser]);

  useEffect(() => {
    if (!isAuthenticated || !isKeyFloUser) return;
    fetchOverview().catch((e) => setError(e.message));
  }, [isAuthenticated, isKeyFloUser, period, chartGrouping, chartMetric, fetchOverview]);

  useEffect(() => {
    if (!searchQuery.trim() || !isKeyFloUser) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/dev/user-analytics/users/search`, {
          params: apiParams({ q: searchQuery.trim(), limit: 8 }),
        });
        setSearchResults(resp.data?.results || []);
      } catch {
        setSearchResults([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery, apiParams, isKeyFloUser]);

  const handleSort = (colKey) => {
    const sortKey = colKey === 'product_display' ? 'name' : colKey;
    if (sort === sortKey) {
      setOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSort(sortKey);
      setOrder('desc');
    }
  };

  useEffect(() => {
    if (!isKeyFloUser || selectedUserId) return;
    fetchUsers().catch((e) => setError(e.message));
  }, [sort, order, isKeyFloUser, selectedUserId, fetchUsers]);

  const headlineMetrics = overview?.headline_metrics || {};
  const chartSeries = overview?.chart?.series || [];
  const loginSeriesEmpty = overview?.chart?.login_series_empty;
  const selectedPeriodLabel = PERIOD_OPTIONS.find((opt) => opt.value === period)?.label || period;

  const sortLabel = TABLE_COLUMNS.find((c) => (c.sortKey || c.key) === sort)?.label || sort;

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
        <p>Please log in</p>
        <button type="button" onClick={() => loginWithRedirect({ authorizationParams: { prompt: 'login' } })}>Log in</button>
      </div>
    );
  }

  if (!isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • User Analytics</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
          <button type="button" className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <DeveloperNavigation />
        </div>
      </div>

      {loading && <LoadingSpinner />}
      {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

      {!loading && overview && (
        <>
          <section style={{ marginBottom: '24px' }}>
            <h3 style={{ marginTop: 0 }}>Platform Activity</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPeriod(opt.value)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '999px',
                    border: '1px solid #e5e7eb',
                    background: period === opt.value ? '#111827' : '#fff',
                    color: period === opt.value ? '#fff' : '#111827',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <h3>Headline Metrics</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px', marginBottom: '20px' }}>
              {[
                ['Contracts Created', headlineMetrics.contracts_created],
                ['Contracts Closed', headlineMetrics.contracts_closed],
                ['Emails Processed', headlineMetrics.emails_processed],
                ['Splits Processed', headlineMetrics.splits_processed],
                ['Forms Generated', headlineMetrics.forms_generated],
              ].map(([label, value]) => (
                <div key={label} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', background: '#f8fafc' }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 600 }}>{value ?? 0}</div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{selectedPeriodLabel.toLowerCase()}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '0.875rem' }}>
                Time grouping
                <select value={chartGrouping} onChange={(e) => setChartGrouping(e.target.value)} style={{ marginLeft: '8px' }}>
                  {CHART_GROUPINGS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </label>
              <label style={{ fontSize: '0.875rem' }}>
                Metric
                <select value={chartMetric} onChange={(e) => setChartMetric(e.target.value)} style={{ marginLeft: '8px' }}>
                  {CHART_METRICS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
            </div>

            <div style={{ height: 280, marginBottom: '8px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {loginSeriesEmpty && (
              <p style={{ color: '#64748b', fontSize: '0.8rem' }}>
                Login metrics populate after the user_login_events table is deployed; pre-deploy history shows as zero.
              </p>
            )}
          </section>

          <section>
            <h3 style={{ marginTop: 0 }}>Users</h3>

            {selectedUserId && factSheet ? (
              <>
                <button
                  type="button"
                  style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, marginBottom: '12px' }}
                  onClick={() => { setSelectedUserId(null); setFactSheet(null); }}
                >
                  ← Back to user leaderboard
                </button>
                {factLoading && <LoadingSpinner />}
                {!factLoading && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '20px' }}>
                      <div>
                        <h2 style={{ marginBottom: '4px' }}>{factSheet.user?.name}</h2>
                        <p style={{ color: '#64748b', margin: 0 }}>{factSheet.user?.email} · User #{factSheet.user?.id}</p>
                        <p style={{ color: '#64748b' }}>
                          {factSheet.user?.phone_number || '—'} · {factSheet.user?.licensed_state || '—'} · {factSheet.user?.brokerage_company_name || '—'}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
                          Joined {formatDateShort(factSheet.user?.created_at)}
                        </div>
                        <div><strong>{factSheet.user?.product_display || factSheet.user?.product_type || '—'}</strong> · {factSheet.user?.payment_user_type || '—'}</div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px', marginBottom: '20px' }}>
                      {[
                        ['Total contracts', factSheet.metrics?.total_contracts],
                        ['Buyer / Seller / Dual', `${factSheet.metrics?.buyer_side_contracts ?? 0} / ${factSheet.metrics?.seller_side_contracts ?? 0} / ${factSheet.metrics?.dual_agency_contracts ?? 0}`],
                        ['Contracts closed', factSheet.metrics?.contracts_closed],
                        ['Emails to KeyFlo', factSheet.metrics?.emails_lifetime],
                        ['Total paid', centsToDisplay(factSheet.metrics?.total_paid_cents)],
                        ['Subscription / Txn fees', `${centsToDisplay(factSheet.metrics?.subscription_fees_cents)} / ${centsToDisplay(factSheet.metrics?.transaction_fees_cents)}`],
                        ['First email', formatDateShort(factSheet.metrics?.first_email_at)],
                        ['Last email', formatDateShort(factSheet.metrics?.last_email_at)],
                        ['Last contract created', formatDateShort(factSheet.metrics?.last_contract_created_at)],
                        ['Contracts 90d (open/closed)', `${factSheet.metrics?.contracts_90d_open ?? 0} / ${factSheet.metrics?.contracts_90d_closed ?? 0}`],
                        ['Emails / splits 90d', `${factSheet.metrics?.emails_90d ?? 0} / ${factSheet.metrics?.splits_90d ?? 0}`],
                        ['Last login / logins 90d', `${formatDateShort(factSheet.metrics?.last_login_at)} · ${factSheet.metrics?.logins_90d ?? 0}`],
                        ['Forms generated', `${factSheet.metrics?.forms_generated ?? 0}${factSheet.metrics?.last_form_generated_at ? ` (last: ${formatDateShort(factSheet.metrics.last_form_generated_at)})` : ''}`],
                      ].map(([label, value]) => (
                        <div key={label} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px' }}>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{label}</div>
                          <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{value ?? '—'}</div>
                        </div>
                      ))}
                    </div>

                    <h4>Open deals</h4>
                    <div style={tableWrapStyle}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <tbody>
                          {(factSheet.open_deals?.contracts || []).map((c) => (
                            <tr key={`c-${c.id}`}>
                              <td style={tdStyle}><strong>Contract #{c.id}</strong> — {c.property_address || '—'}</td>
                              <td style={tdStyle}><span style={linkStyle} onClick={() => navigate(`/contract/${c.id}`)} role="link" tabIndex={0} onKeyDown={() => {}}>View →</span></td>
                            </tr>
                          ))}
                          {(factSheet.open_deals?.listings || []).map((l) => (
                            <tr key={`l-${l.id}`}>
                              <td style={tdStyle}><strong>Listing #{l.id}</strong> — {l.street_address || '—'}</td>
                              <td style={tdStyle}><span style={linkStyle} onClick={() => navigate(`/listing/${l.id}`)} role="link" tabIndex={0} onKeyDown={() => {}}>View →</span></td>
                            </tr>
                          ))}
                          {(factSheet.open_deals?.buyer_agreements || []).map((ba) => (
                            <tr key={`ba-${ba.id}`}>
                              <td style={tdStyle}><strong>Buyer Agreement #{ba.id}</strong> — {ba.street_address || '—'}</td>
                              <td style={tdStyle}><span style={linkStyle} onClick={() => navigate(`/buyer-agreement/${ba.id}`)} role="link" tabIndex={0} onKeyDown={() => {}}>View →</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', marginBottom: '16px', position: 'relative' }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by user ID, name, or email…"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                  />
                  <button type="button" className="back-button" onClick={() => setSearchQuery('')}>Clear</button>
                  {searchResults.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
                      border: '1px solid #e5e7eb', borderRadius: '6px', zIndex: 10, marginTop: '4px',
                    }}>
                      {searchResults.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => { setSearchQuery(''); setSearchResults([]); fetchFactSheet(r.id); }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none', background: '#fff', cursor: 'pointer' }}
                        >
                          {r.name} · {r.email} · #{r.id}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  Showing top 20 users · sorted by <strong>{sortLabel}</strong> {order === 'desc' ? '↓' : '↑'}
                </p>

                <div style={tableWrapStyle}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        {TABLE_COLUMNS.map((col) => {
                          const sortKey = col.sortKey || col.key;
                          const isSorted = sort === sortKey;
                          const sortable = !['product_display', 'payment_user_type'].includes(col.key);
                          return (
                            <th
                              key={col.key}
                              style={{ ...thStyle, color: isSorted ? '#2563eb' : undefined, cursor: sortable ? 'pointer' : 'default' }}
                              onClick={sortable ? () => handleSort(sortKey) : undefined}
                            >
                              {col.label}{isSorted ? (order === 'desc' ? ' ↓' : ' ↑') : ''}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((row) => (
                        <tr key={row.id}>
                          {TABLE_COLUMNS.map((col) => {
                            const val = row[col.key];
                            const clickable = ['id', 'name', 'email'].includes(col.key);
                            return (
                              <td key={col.key} style={tdStyle}>
                                {clickable ? (
                                  <span
                                    style={linkStyle}
                                    onClick={() => fetchFactSheet(row.id)}
                                    role="link"
                                    tabIndex={0}
                                    onKeyDown={() => {}}
                                  >
                                    {col.key === 'created_at' ? formatDateShort(val) : val}
                                  </span>
                                ) : (
                                  col.key === 'created_at' ? formatDateShort(val) : (val ?? '—')
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default DeveloperUserAnalytics;
